/**
 * Settings — the paycheck plan, the sorting rules, the trash, and the session.
 *
 * The paycheck plan is the one place a number can be wrong in a way that makes
 * every other number wrong, so it shows the arithmetic as you type instead of
 * waiting for a save to reveal a mismatch.
 */

import { useEffect, useState } from 'react';
import {
  formatAmountInput,
  formatMoney,
  ledgerToday,
  listedPayDays,
  merchantName,
  monthlyAmountOf,
  normalizePayDays,
  ordinalDay,
  parseMoney,
  paycheckRemainder,
  perPaycheckFromMonthly,
  roundCents,
  shortDate,
  suggestedPayDay,
  sumCents,
  type SortingRule,
} from '@alohamula/domain';
import { PageHeader } from '../components/AppShell';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  MoneyInput,
  Money,
  Section,
  SegmentedControl,
  Select,
  Skeleton,
  Toggle,
} from '../components/ui';
import {
  applyTheme,
  readPreference,
  watchSystemTheme,
  writePreference,
  type ThemePreference,
} from '../theme';
import {
  DEFAULT_HOME_LAYOUT,
  HOME_SECTION_META,
  readHomeLayout,
  writeHomeLayout,
  type HomeLayout as HomeLayoutState,
  type HomeSection,
} from '../homeLayout';
import { useToast } from '../components/ui/ToastProvider';
import {
  useDeleteSortingRule,
  useUpdateSortingRule,
  useRestoreTransaction,
  useTrashedTransactions,
  useUpdatePaycheckSettings,
} from '../api/queries';
import { useAuth } from '../auth/AuthProvider';
import { colorVar } from '../budget/colors';
import { TrashIcon } from '../components/icons';
import { RuleInvestigation } from '../components/AssistantCards';
import type { Budget } from '../budget/useBudget';
import './pages.css';

interface SettingsPageProps {
  budget: Budget;
  now: Date;
}

export function SettingsPage({ budget, now }: SettingsPageProps) {
  return (
    <>
      <PageHeader title="Settings" period={budget.period} />
      <PaycheckPlan budget={budget} />
      <RolloverKickoff budget={budget} now={now} />
      <SortingRules budget={budget} now={now} />
      <Appearance />
      <HomeLayout />
      <Trash now={now} />
      <Account />
    </>
  );
}

const APPEARANCES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
] as const satisfies readonly { value: ThemePreference; label: string }[];

function HomeLayout() {
  const [layout, setLayout] = useState<HomeLayoutState>(readHomeLayout);
  const hidden = new Set(layout.hidden);

  function commit(next: HomeLayoutState) {
    setLayout(writeHomeLayout(next));
  }

  function move(index: number, direction: -1 | 1) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= layout.order.length) return;
    const order = [...layout.order];
    const swap = order[index];
    order[index] = order[nextIndex];
    order[nextIndex] = swap;
    commit({ ...layout, order });
  }

  function setShown(section: HomeSection, shown: boolean) {
    const next = new Set(layout.hidden);
    if (shown) next.delete(section);
    else next.add(section);
    commit({ ...layout, hidden: [...next] });
  }

  return (
    <Section title="Home">
      <div className="card">
        <p className="field__hint" style={{ padding: 'var(--space-md) var(--space-md) 0' }}>
          Leftover stays at the top. Hide a section or move it. Saved in this browser — the
          phone keeps its own setting.
        </p>
        <div className="pin-list">
          {layout.order.map((section, index) => {
            const shown = !hidden.has(section);
            return (
              <div className={`pin-row${shown ? '' : ' pin-row--dim'}`} key={section}>
                <Toggle
                  label={HOME_SECTION_META[section].title}
                  hint={HOME_SECTION_META[section].subtitle}
                  checked={shown}
                  onChange={(value) => setShown(section, value)}
                />
                <div className="layout-move">
                  <Button
                    size="sm"
                    aria-label={`Move ${HOME_SECTION_META[section].title} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    Up
                  </Button>
                  <Button
                    size="sm"
                    aria-label={`Move ${HOME_SECTION_META[section].title} down`}
                    disabled={index === layout.order.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    Down
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: 'var(--space-sm) var(--space-md) var(--space-md)' }}>
          <Button size="sm" onClick={() => commit(DEFAULT_HOME_LAYOUT)}>
            Reset to default
          </Button>
        </div>
      </div>
    </Section>
  );
}

function Appearance() {
  const [preference, setPreference] = useState<ThemePreference>(readPreference);

  // A window sitting on System should follow the device when it flips at sunset,
  // without needing a reload.
  useEffect(() => {
    if (preference !== 'system') return;
    return watchSystemTheme(() => applyTheme('system'));
  }, [preference]);

  return (
    <Section title="Appearance">
      <div className="card card--padded row" style={{ justifyContent: 'space-between' }}>
        <div>
          <p className="field__label">Theme</p>
          <span className="field__hint">
            {preference === 'system'
              ? 'Following this device.'
              : 'Saved for this browser. Your phone keeps its own setting.'}
          </span>
        </div>
        <SegmentedControl
          label="Theme"
          value={preference}
          options={APPEARANCES}
          onChange={(next) => {
            setPreference(next);
            writePreference(next);
          }}
        />
      </div>
    </Section>
  );
}

/**
 * States the gap and its direction, rather than leaving the reader to subtract
 * the plan from a total. `undefined` when the plan agrees, so no warning shows.
 */
function driftMessage(drift: number, actual: number, noun: string): string | undefined {
  if (Math.abs(drift) < 0.01) return undefined;
  const gap = formatMoney(Math.abs(drift));
  return drift > 0
    ? `Plan is ${gap} above your ${noun}, which total ${formatMoney(actual)}`
    : `Plan is ${gap} below your ${noun}, which total ${formatMoney(actual)}`;
}

function sameDays(left: readonly number[], right: readonly number[]): boolean {
  return left.length === right.length && left.every((day, index) => day === right[index]);
}

function PaycheckPlan({ budget }: { budget: Budget }) {
  const settings = budget.snapshot.paycheckSettings;
  const savedDays = normalizePayDays(settings.payDays ?? []);
  const [paycheck, setPaycheck] = useState(formatAmountInput(settings.paycheckAmount));
  const [envelopes, setEnvelopes] = useState(formatAmountInput(settings.envelopeAllocation));
  const [bills, setBills] = useState(formatAmountInput(settings.billAllocation));
  const [payDays, setPayDaysDraft] = useState(savedDays);
  const [failure, setFailure] = useState<string | null>(null);

  const update = useUpdatePaycheckSettings();
  const toast = useToast();

  const paycheckValue = parseMoney(paycheck);
  const envelopesValue = parseMoney(envelopes);
  const billsValue = parseMoney(bills);
  const allParse = paycheckValue != null && envelopesValue != null && billsValue != null;

  const dirty =
    paycheckValue !== settings.paycheckAmount ||
    envelopesValue !== settings.envelopeAllocation ||
    billsValue !== settings.billAllocation ||
    !sameDays(payDays, savedDays);

  /*
   * What the envelopes and bills actually add up to, which is the number the
   * plan should agree with. A plan that says $2,224 of envelopes while the
   * envelopes total $2,400 is the quiet way a budget stops meaning anything.
   *
   * A month of funding halved, rather than whatever this period happens to
   * fund: an item paid for out of a single paycheck makes the two halves of the
   * month uneven, and comparing a plan against whichever half today falls in
   * would flip the warning every two weeks over a plan nobody touched.
   */
  const actualEnvelopes = perPaycheckFromMonthly(
    sumCents(budget.envelopes.map((balance) => monthlyAmountOf(balance.item)))
  );
  const actualBills = perPaycheckFromMonthly(
    sumCents(budget.bills.map((balance) => monthlyAmountOf(balance.item)))
  );

  /*
   * The check is against the envelopes and bills that exist, not against the two
   * numbers typed above them.
   *
   * Measuring the plan against itself is how this screen came to show a green
   * "Balanced" badge over a genuinely unassigned $213: the plan claimed $2,224.11
   * of envelopes and $1,955.92 of bills, which sums to the paycheck exactly,
   * while the real envelopes totalled $1,960.11 and the real bills $2,006.92.
   * The two errors nearly cancelled, so the total looked clean and the badge
   * reported success. A reassurance that survives its own contradiction is worse
   * than no reassurance.
   */
  const leftover = allParse
    ? paycheckRemainder(paycheckValue, actualBills, actualEnvelopes)
    : 0;

  const envelopeDrift = envelopesValue != null ? envelopesValue - actualEnvelopes : 0;
  const billDrift = billsValue != null ? billsValue - actualBills : 0;
  const hasDrift = Math.abs(envelopeDrift) >= 0.01 || Math.abs(billDrift) >= 0.01;

  async function save() {
    if (!allParse) return;
    setFailure(null);
    try {
      await update.mutateAsync({
        paycheckAmount: paycheckValue,
        envelopeAllocation: envelopesValue,
        billAllocation: billsValue,
        payDays,
        expectedVersion: settings.version,
      });
      toast.show('Paycheck plan saved');
    } catch (error) {
      setFailure(
        error instanceof Error ? error.message : 'Could not save. Try again.'
      );
    }
  }

  return (
    <Section
      title="Paycheck plan"
      meta={`${formatMoney(roundCents((paycheckValue ?? settings.paycheckAmount) * payDays.length))} a month`}
    >
      <div className="card" style={{ padding: 'var(--space-md)' }}>
        <div className="settings-grid">
          <Field label="Paycheck" hint={`What lands on ${listedPayDays(payDays)}`}>
            {({ id }) => (
              <MoneyInput
                id={id}
                value={paycheck}
                onChange={(event) => setPaycheck(event.target.value)}
              />
            )}
          </Field>
          <Field
            label="Planned for envelopes"
            warning={driftMessage(envelopeDrift, actualEnvelopes, 'envelopes')}
          >
            {({ id }) => (
              <MoneyInput
                id={id}
                value={envelopes}
                onChange={(event) => setEnvelopes(event.target.value)}
              />
            )}
          </Field>
          <Field
            label="Planned for bills"
            warning={driftMessage(billDrift, actualBills, 'bills')}
          >
            {({ id }) => (
              <MoneyInput
                id={id}
                value={bills}
                onChange={(event) => setBills(event.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="pay-days" style={{ marginTop: 'var(--space-md)' }}>
          <p className="headline__note">Pay days</p>
          {payDays.map((day, index) => (
            <div key={`${index}-${day}`} className="pay-days__row">
              <Field label={`On the ${ordinalDay(day)}`}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    max={31}
                    value={day}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      if (!Number.isInteger(next)) return;
                      setPayDaysDraft(
                        normalizePayDays(payDays.map((value, row) => (row === index ? next : value)))
                      );
                    }}
                  />
                )}
              </Field>
              {payDays.length > 1 && (
                <Button
                  emphasis="ghost"
                  size="sm"
                  onClick={() =>
                    setPayDaysDraft(normalizePayDays(payDays.filter((_, row) => row !== index)))
                  }
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          {suggestedPayDay(payDays) != null && (
            <Button
              emphasis="ghost"
              size="sm"
              onClick={() => {
                const next = suggestedPayDay(payDays);
                if (next != null) setPayDaysDraft(normalizePayDays([...payDays, next]));
              }}
            >
              Add a pay day
            </Button>
          )}
          <p className="headline__note">
            Each date is when a paycheck lands. Leftover follows the stretch until the next one.
          </p>
        </div>

        {/* Reads on the paycheck against real envelopes and bills. "Balanced" is
            only reachable when nothing above it is warning. */}
        <div className="plan-check" style={{ marginTop: 'var(--space-md)' }}>
          {allParse ? (
            <>
              <span>
                {leftover === 0
                  ? hasDrift
                    ? 'Your envelopes and bills use the whole paycheck, but the plan above disagrees with them.'
                    : 'Every dollar of the paycheck is assigned.'
                  : leftover > 0
                    ? 'Not assigned to any envelope or bill. You can leave it — Overview will show the gap.'
                    : 'Your envelopes and bills need more than the paycheck'}
              </span>
              {leftover === 0 && !hasDrift ? (
                <Badge tone="success">Balanced</Badge>
              ) : leftover === 0 ? (
                <Badge tone="warning">Plan is stale</Badge>
              ) : (
                <Money value={Math.abs(leftover)} tone={leftover < 0 ? 'over' : 'default'} />
              )}
            </>
          ) : (
            <span>Amounts must be numbers, like 2224.11.</span>
          )}
        </div>

        {failure && (
          <p className="field__error" role="alert" style={{ marginTop: 'var(--space-xs)' }}>
            {failure}
          </p>
        )}

        <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-md)' }}>
          <Button
            emphasis="primary"
            loading={update.isPending}
            disabled={!allParse || !dirty}
            onClick={() => void save()}
          >
            Save plan
          </Button>
          {dirty && (
            <Button
              emphasis="ghost"
              onClick={() => {
                setPaycheck(formatAmountInput(settings.paycheckAmount));
                setEnvelopes(formatAmountInput(settings.envelopeAllocation));
                setBills(formatAmountInput(settings.billAllocation));
                setPayDaysDraft(savedDays);
              }}
            >
              Discard changes
            </Button>
          )}
        </div>
      </div>
    </Section>
  );
}

function RolloverKickoff({ budget, now }: { budget: Budget; now: Date }) {
  const settings = budget.snapshot.paycheckSettings;
  const saved = settings.rolloverKickoffDate ?? '';
  const [draft, setDraft] = useState(saved || ledgerToday(now));
  const [failure, setFailure] = useState<string | null>(null);
  const update = useUpdatePaycheckSettings();
  const toast = useToast();

  useEffect(() => {
    setDraft(saved || ledgerToday(now));
  }, [saved, now]);

  const dirty = (draft || null) !== (saved || null);

  async function save(next: string | null) {
    setFailure(null);
    try {
      await update.mutateAsync({
        rolloverKickoffDate: next,
        expectedVersion: settings.version,
      });
      toast.show(next ? 'Rollover start date saved' : 'Rollover start date cleared');
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not save. Try again.');
    }
  }

  return (
    <Section title="Rollover">
      <div className="card card--padded">
        <Field
          label="Start date"
          hint={
            saved
              ? `Leftover from paychecks that began before ${shortDate(saved, now)} is ignored. Charges still count in their own period.`
              : 'Leftover from paychecks that began before this day is ignored. Use this when you restart the books mid-month.'
          }
        >
          {({ id }) => (
            <Input
              id={id}
              type="date"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          )}
        </Field>

        {failure && (
          <p className="field__error" role="alert" style={{ marginTop: 'var(--space-xs)' }}>
            {failure}
          </p>
        )}

        <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-md)' }}>
          <Button
            emphasis="primary"
            loading={update.isPending}
            disabled={!draft || !dirty}
            onClick={() => void save(draft)}
          >
            Save start date
          </Button>
          <Button
            emphasis="secondary"
            loading={update.isPending}
            disabled={saved === ledgerToday(now)}
            onClick={() => void save(ledgerToday(now))}
          >
            Start fresh today
          </Button>
          {saved ? (
            <Button emphasis="ghost" loading={update.isPending} onClick={() => void save(null)}>
              Clear
            </Button>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

function addedOn(createdAt: string, now: Date): string {
  const day = createdAt.slice(0, 10);
  return day ? `Added ${shortDate(day, now)}` : 'Added recently';
}

function SortingRules({ budget, now }: { budget: Budget; now: Date }) {
  const rules = budget.snapshot.sortingRules.filter((rule) => rule.isActive);
  const remove = useDeleteSortingRule();
  const update = useUpdateSortingRule();
  const toast = useToast();
  const [editing, setEditing] = useState<SortingRule | null>(null);
  const [keyword, setKeyword] = useState('');
  const [budgetItemId, setBudgetItemId] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  function openEdit(rule: SortingRule) {
    setEditing(rule);
    setKeyword(rule.keyword);
    setBudgetItemId(rule.budgetItemId);
    setFailure(null);
  }

  async function saveEdit() {
    if (!editing) return;
    const nextKeyword = keyword.trim();
    if (!nextKeyword || !budgetItemId) {
      setFailure('A keyword and an envelope are required.');
      return;
    }
    try {
      await update.mutateAsync({ id: editing.id, keyword: nextKeyword, budgetItemId });
      toast.show(`Rule updated to “${nextKeyword}”`);
      setEditing(null);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not save that rule.');
    }
  }

  return (
    <Section title="Sorting rules" meta={`${rules.length}`}>
      <div className="card">
        <RuleInvestigation budget={budget} />
        {rules.length === 0 ? (
          <EmptyState
            title="No rules yet"
            body="Rules are made while sorting: file a charge with “Remember my choices” on, and the merchant is filed the same way next time."
          />
        ) : (
          rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              itemName={budget.itemsById.get(rule.budgetItemId)?.name}
              itemColor={budget.itemsById.get(rule.budgetItemId)?.color}
              added={addedOn(rule.createdAt, now)}
              busy={remove.isPending || update.isPending}
              onEdit={() => openEdit(rule)}
              onDelete={async () => {
                await remove.mutateAsync(rule.id);
                toast.show(`Rule for “${rule.keyword}” removed`);
              }}
            />
          ))
        )}
      </div>
      <Dialog
        open={editing != null}
        title="Edit rule"
        subtitle={editing ? addedOn(editing.createdAt, now) : undefined}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button emphasis="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button emphasis="primary" loading={update.isPending} onClick={() => void saveEdit()}>
              Save
            </Button>
          </>
        }
      >
        <div className="stack" style={{ gap: 'var(--space-md)' }}>
        <Field label="Match keyword" hint="Future charges that contain this keyword are filed under the envelope below." error={failure ?? undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              value={keyword}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(event) => setKeyword(event.target.value)}
            />
          )}
        </Field>
        <Field label="Sort to">
          {({ id }) => (
            <Select
              id={id}
              value={budgetItemId}
              onChange={(event) => setBudgetItemId(event.target.value)}
            >
              {budget.activeItems
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        </div>
      </Dialog>
    </Section>
  );
}

function RuleRow({
  rule,
  itemName,
  itemColor,
  added,
  busy,
  onEdit,
  onDelete,
}: {
  rule: SortingRule;
  itemName?: string;
  itemColor?: string;
  added: string;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rule">
      <div className="rule__main">
        <div className="rule__line">
          <span className="rule__keyword">{rule.keyword}</span>
          <span className="rule__arrow" aria-hidden="true">
            →
          </span>
          <span className="row" style={{ gap: 'var(--space-xxs)' }}>
            {itemColor && (
              <span className="dot" style={{ background: colorVar(itemColor) }} aria-hidden="true" />
            )}
            {itemName ?? <em>Deleted category</em>}
          </span>
        </div>
        <span className="rule__added">{added}</span>
      </div>
      <div className="spacer" />
      <Button size="sm" emphasis="ghost" disabled={busy} onClick={onEdit}>
        Edit
      </Button>
      <Button
        size="sm"
        emphasis="ghost"
        aria-label={`Delete the rule for ${rule.keyword}`}
        disabled={busy}
        onClick={onDelete}
      >
        <TrashIcon width={16} height={16} />
      </Button>
    </div>
  );
}

/**
 * Trash is loaded on demand rather than with the snapshot, since it is the one
 * screen nobody visits on a normal day.
 */
function Trash({ now }: { now: Date }) {
  const [open, setOpen] = useState(false);
  const trashed = useTrashedTransactions(open);
  const restore = useRestoreTransaction();
  const toast = useToast();

  const transactions = (trashed.data?.transactions ?? []).filter(
    (transaction) => transaction.deletedAt != null
  );

  return (
    <Section
      title="Trash"
      action={
        <Button size="sm" onClick={() => setOpen((current) => !current)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {open && (
        <div className="card">
          {trashed.isLoading ? (
            <div style={{ padding: 'var(--space-md)' }}>
              <Skeleton height={44} />
            </div>
          ) : transactions.length === 0 ? (
            <EmptyState title="Nothing in the trash" />
          ) : (
            transactions.map((transaction) => (
              <div className="rule" key={transaction.id}>
                <span>{merchantName(transaction.description)}</span>
                <span className="rule__arrow">·</span>
                <span className="numeric">{formatMoney(Math.abs(transaction.amount))}</span>
                <span className="rule__arrow">·</span>
                <span>{shortDate(transaction.transactionDate, now)}</span>
                <div className="spacer" />
                <Button
                  size="sm"
                  disabled={restore.isPending}
                  onClick={async () => {
                    await restore.mutateAsync({
                      id: transaction.id,
                      expectedVersion: transaction.version,
                    });
                    toast.show('Restored');
                  }}
                >
                  Restore
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </Section>
  );
}

function Account() {
  const { state, signOut } = useAuth();
  return (
    <Section title="Account">
      <div className="card rule">
        <span>{state.status === 'signedIn' ? state.session.email : 'Signed in'}</span>
        <div className="spacer" />
        <Button onClick={signOut}>Sign out</Button>
      </div>
    </Section>
  );
}
